import type { MaybeRefOrGetter } from 'vue'

import {
  useCurrentElement,
  useElementVisibility,
  useParentElement,
  toValue,
  tryOnMounted,
  unrefElement,
} from '@vueuse/core'
import { watch } from 'vue'

/**
 * A composable that allows you to expand an ancestor element to fit the current element.
 * This composable should only be used when you can't use CSS to achieve the same effect or when the parent element is not under your control (e.g. a third party component).
 * @param currentElement - The element to expand the ancestor to fit, if empty, the current element will be used
 * @param options.ancestor - The selector of the ancestor to expand. If not provided, the direct parent element will be used
 * @param options.enabled - Whether to enable the composable at all
 * @param options.heightOffset - The offset to add to the height of the parent element
 * @param options.widthOffset - The offset to add to the width of the parent element
 */
export default function useExpandAncestorToFit(
  currentElement: MaybeRefOrGetter<
    HTMLElement | SVGElement | null | undefined
  > = useCurrentElement<HTMLElement | SVGAElement>(),
  {
    ancestor,
    enabled,
    heightOffset,
    widthOffset,
  }: {
    ancestor?: string
    enabled?: boolean
    heightOffset?: number
    widthOffset?: number
  } = {}
): void {
  const targetIsVisible = useElementVisibility(currentElement)

  function expandAncestorToFit(): void {
    if (!enabled || !targetIsVisible.value) {
      return
    }

    const element = unrefElement(currentElement)

    if (!element) {
      return
    }

    const parent = ancestor
      ? (element.closest(ancestor) as HTMLElement)
      : (toValue(useParentElement(element)) as HTMLElement)

    if (!parent) {
      return
    }

    const elementRect = element.getBoundingClientRect()
    const parentRect = parent.getBoundingClientRect()

    if (elementRect.width > parentRect.width) {
      const effectiveWidthOffset = widthOffset ?? 0
      parent.style.width = `${elementRect.width + effectiveWidthOffset}px`
    }

    if (elementRect.height > parentRect.height) {
      const effectiveHeightOffset = heightOffset ?? 0
      parent.style.height = `${elementRect.height + effectiveHeightOffset}px`
    }
  }

  tryOnMounted(expandAncestorToFit)
  watch(() => toValue(currentElement), expandAncestorToFit)
  watch(targetIsVisible, (newValue, oldValue) => {
    if (newValue && !oldValue) {
      expandAncestorToFit()
    }
  })
}
